import { useEffect, useState } from "react";
import api, { errMsg } from "../api";
import { MODULES, clientName } from "../utils";
import { normText } from "../components/ClientCombobox";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, X } from "lucide-react";

const EMPTY = { first_name: "", last_name: "", name: "", contact_person: "", type: "particulier", is_location_service: false, email: "", phone: "", street: "", address_info: "", zip: "", city: "", country: "Suisse", discounts: { conciergerie: 0, laundry: 0, nettoyage: 0 }, notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");

  const q = normText(search);
  const filtered = q
    ? clients.filter((c) => normText([c.name, c.first_name, c.last_name, c.contact_person, c.zip, c.city, c.email, c.phone].filter(Boolean).join(" ")).includes(q))
    : clients;

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c, discounts: { ...EMPTY.discounts, ...(c.discounts || {}) } }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, discounts: Object.fromEntries(Object.entries(form.discounts).map(([k, v]) => [k, parseFloat(v) || 0])) };
      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Client modifié" : "Client créé");
      setOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer le client « ${c.name} » ?`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Client supprimé"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const field = (key, label, type = "text", extra = {}) => (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
      <input data-testid={`client-form-${key}`} type={type} value={form[key] ?? ""} {...extra}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:outline-none" />
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="clients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Clients</h1>
        <button data-testid="create-client-button" onClick={openCreate}
          className="flex items-center gap-2 bg-slate-900 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors">
          <Plus size={16} /> Nouveau client
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="clients-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (nom, société, dirigeant, localité, email, tél)…"
            className="w-96 max-w-full rounded-lg border border-slate-300 pl-9 pr-8 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:outline-none" />
          {search && (
            <button data-testid="clients-search-clear" onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={14} /></button>
          )}
        </div>
        <span className="text-xs text-slate-400" data-testid="clients-count">{filtered.length} client{filtered.length > 1 ? "s" : ""}</span>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
              <th className="px-5 py-3">Nom</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Contact</th>
              <th className="px-5 py-3">Rabais par secteur</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="clients-table">
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`client-row-${c.id}`} className="hover:bg-slate-50">
                <td className="px-5 py-3 font-medium text-slate-900">{clientName(c)}</td>
                <td className="px-5 py-3 capitalize text-slate-600">{c.type}{c.is_location_service && <span className="ml-2 text-[11px] font-medium px-2 py-0.5 rounded-md bg-rose-50 text-rose-700">Location</span>}</td>
                <td className="px-5 py-3 text-slate-600">{c.email || c.phone || "—"}</td>
                <td className="px-5 py-3 text-slate-600 text-xs">{Object.keys(MODULES).filter((m) => ((c.discounts?.[m] ?? c.discount_percent) || 0) > 0).map((m) => `${MODULES[m].label} ${c.discounts?.[m] ?? c.discount_percent} %`).join(" · ") || "—"}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="text-slate-400 hover:text-slate-900 p-1"><Pencil size={15} /></button>
                  <button data-testid={`delete-client-${c.id}`} onClick={() => remove(c)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && clients.length > 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400" data-testid="clients-empty-search">Aucun client trouvé.</td></tr>}
            {clients.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">Aucun client. Créez votre premier client.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="client-form-modal">
          <form onSubmit={save} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-base font-semibold text-slate-900">{editing ? "Modifier le client" : "Nouveau client"}</h2>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</label>
              <select data-testid="client-form-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-slate-900 focus:outline-none">
                <option value="particulier">Particulier</option><option value="entreprise">Société</option>
              </select>
            </div>
            {form.type === "entreprise" ? (
              <>
                {field("name", "Nom *", "text", { required: true })}
                {field("contact_person", "Dirigeant (facultatif)")}
              </>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {field("last_name", "Nom *", "text", { required: true })}
                {field("first_name", "Prénom *", "text", { required: true })}
              </div>
            )}
            {field("street", "Adresse et numéro *", "text", { required: true })}
            {field("address_info", "Information supplémentaire (facultatif)")}
            <div className="grid grid-cols-2 gap-3">
              {field("zip", "CP *", "text", { required: true })}
              {field("city", "Ville *", "text", { required: true })}
            </div>
            {field("country", "Pays")}
            <div className="grid grid-cols-2 gap-3">
              {field("email", "Email (facultatif)", "email")}
              {field("phone", "Tél (facultatif)")}
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="client-form-location" type="checkbox" checked={!!form.is_location_service} onChange={(e) => setForm({ ...form, is_location_service: e.target.checked })} />
              Client du service location
            </label>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Rabais par secteur (%)</label>
              <div className="grid grid-cols-3 gap-3 mt-1">
                {Object.entries(MODULES).map(([m, M]) => (
                  <div key={m}>
                    <label className="text-[11px] font-semibold" style={{ color: M.color }}>{M.label}</label>
                    <input data-testid={`client-form-rabais-${m}`} type="number" min="0" max="100" step="0.5"
                      value={form.discounts?.[m] ?? 0}
                      onChange={(e) => setForm({ ...form, discounts: { ...form.discounts, [m]: e.target.value } })}
                      className="mt-0.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>
            {field("notes", "Notes")}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="client-form-cancel" onClick={() => setOpen(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="client-form-submit"
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800">Enregistrer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
